/**
 * useReviews Hook
 *
 * Fetches reviews for a listing from Firestore.
 * Returns empty reviews until real review data is available.
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../../services/firebase';

export interface ReviewGuest {
  id: string;
  displayName: string;
  photoURL?: string;
}

export interface Review {
  id: string;
  guest: ReviewGuest;
  rating: number;
  content: string;
  createdAt: string;
}

export interface ReviewsData {
  items: Review[];
  averageRating: number;
  totalCount: number;
}

interface UseReviewsReturn {
  reviews: ReviewsData | null;
  isLoading: boolean;
  error: Error | null;
}

export function useReviews(listingId: string): UseReviewsReturn {
  const [reviews, setReviews] = useState<ReviewsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchReviews() {
      if (!listingId) {
        setReviews({ items: [], averageRating: 0, totalCount: 0 });
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch reviews from Firestore
        const reviewsRef = collection(db, 'reviews');
        const q = query(
          reviewsRef,
          where('listingId', '==', listingId),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const reviewItems: Review[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          // Skip admin-hidden (moderated) reviews.
          if (data.hidden === true) return;
          reviewItems.push({
            id: doc.id,
            guest: {
              id: data.guestId || '',
              displayName: data.guestName || 'Guest',
              photoURL: data.guestPhotoURL,
            },
            rating: data.rating || 0,
            content: data.content || '',
            createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt || '',
          });
        });

        const avgRating = reviewItems.length > 0
          ? reviewItems.reduce((sum, r) => sum + r.rating, 0) / reviewItems.length
          : 0;

        setReviews({
          items: reviewItems,
          averageRating: avgRating,
          totalCount: reviewItems.length,
        });
      } catch (err) {
        console.error('Error fetching reviews:', err);
        // Return empty reviews on error (no mock data)
        setReviews({ items: [], averageRating: 0, totalCount: 0 });
        setError(err instanceof Error ? err : new Error('Failed to fetch reviews'));
      } finally {
        setIsLoading(false);
      }
    }

    fetchReviews();
  }, [listingId]);

  return { reviews, isLoading, error };
}

export default useReviews;
